<script lang="ts">
  import BulkPromptInput from './BulkPromptInput.svelte'
  import { 
    Check, 
    X, 
    MessageSquare, 
    PenLine, 
    RefreshCw, 
    Maximize2, 
    Minimize2 
  } from 'lucide-svelte'
  
  interface Props {
    mode: 'splitview' | 'inlineview'
    
    // Stats for diff display
    showStats?: boolean
    addedChars?: number
    deletedChars?: number
    
    // State
    isProcessing?: boolean
    hasItems?: boolean
    itemsCount?: number
    
    // Conditional display for AI tools
    showExpand?: boolean
    showShrink?: boolean
    showPrompt?: boolean
    
    // Event handlers
    onApplyAll?: () => void
    onRejectAll?: () => void
    onContinueAll?: () => void
    onParaphraseAll?: () => void
    onExpandAll?: () => void
    onShrinkAll?: () => void
    onPromptAll?: () => void
    
    // Bulk prompt state and handlers
    showBulkPrompt?: boolean
    bulkPromptInput?: string
    currentModelId?: string
    onBulkPromptSubmit?: (prompt: string, modelId: string) => void
    onBulkPromptClose?: () => void
    onModelChange?: (modelId: string) => void
  }
  
  let {
    mode = 'splitview',
    showStats = false,
    addedChars = 0,
    deletedChars = 0,
    isProcessing = false,
    hasItems = false,
    itemsCount = 0,
    showExpand = true,
    showShrink = true,
    showPrompt = true,
    onApplyAll,
    onRejectAll,
    onContinueAll,
    onParaphraseAll,
    onExpandAll,
    onShrinkAll,
    onPromptAll,
    showBulkPrompt = false,
    bulkPromptInput = $bindable(''),
    currentModelId = $bindable(''),
    onBulkPromptSubmit,
    onBulkPromptClose,
    onModelChange
  }: Props = $props()
  
  // Local state for bulk prompt
  let localShowBulkPrompt = $state(showBulkPrompt)
  let savedBulkPromptInput = $state('')
  
  $effect(() => {
    localShowBulkPrompt = showBulkPrompt
  })
  
  // Handle bulk prompt click
  function handleBulkPromptClick() {
    if (localShowBulkPrompt) {
      // Save input before closing
      if (bulkPromptInput.trim()) {
        savedBulkPromptInput = bulkPromptInput
      }
      onBulkPromptClose?.()
    } else {
      // Restore saved input when opening
      if (!bulkPromptInput && savedBulkPromptInput) {
        bulkPromptInput = savedBulkPromptInput
      }
      onPromptAll?.()
    }
  }
  
  // Handle bulk prompt submission
  function handleBulkPromptSubmit() {
    if (!bulkPromptInput.trim() || !currentModelId) return
    
    onBulkPromptSubmit?.(bulkPromptInput, currentModelId)
    
    // Clear the input after successful submission
    bulkPromptInput = ''
    savedBulkPromptInput = ''
  }
  
  // Determine icon and button sizes based on mode
  const iconSize = $derived(16)  // 통일된 아이콘 크기
  const buttonSize = $derived(26)  // 통일된 버튼 크기
  const hasAIStats = $derived(showStats && (addedChars > 0 || deletedChars > 0))
</script>

<div class="ai-actions-header" data-mode={mode}>
  <div class="ai-actions-toolbar">
    <!-- Apply All -->
    {#if onApplyAll}
      <button
        class="ai-action-btn ai-action-apply"
        onclick={onApplyAll}
        disabled={!hasItems && !isProcessing}
        title={itemsCount > 0 ? `모두 적용 (${itemsCount}개)` : '적용할 항목 없음'}
        aria-label="모두 적용"
      >
        <Check size={iconSize} />
      </button>
    {/if}
    
    <!-- Reject/Exit -->
    {#if onRejectAll}
      <button
        class="ai-action-btn ai-action-reject"
        onclick={onRejectAll}
        title={mode === 'splitview' ? "에디터로 돌아가기" : "모두 무시"}
        aria-label={mode === 'splitview' ? "에디터로 돌아가기" : "모두 무시"}
      >
        <X size={iconSize} />
      </button>
    {/if}
    
    <!-- Divider -->
    <div class="toolbar-separator"></div>
    
    <!-- Prompt All (first AI tool) -->
    {#if showPrompt && onPromptAll}
      <button
        class="ai-action-btn ai-tool-btn has-ai-label"
        onclick={handleBulkPromptClick}
        disabled={isProcessing}
        title={isProcessing ? "처리 중..." : "프롬프트로 편집"}
        aria-label="프롬프트로 편집"
      >
        <span class="ai-label">AI</span>
        <MessageSquare size={iconSize} />
      </button>
    {/if}
    
    <!-- Continue All -->
    {#if onContinueAll}
      <button
        class="ai-action-btn ai-tool-btn has-ai-label"
        onclick={onContinueAll}
        disabled={isProcessing}
        title="모두 이어쓰기"
        aria-label="모두 이어쓰기"
      >
        <span class="ai-label">AI</span>
        <PenLine size={iconSize} />
      </button>
    {/if}
    
    <!-- Paraphrase All -->
    {#if onParaphraseAll}
      <button
        class="ai-action-btn ai-tool-btn has-ai-label"
        onclick={onParaphraseAll}
        disabled={isProcessing}
        title={isProcessing ? "처리 중..." : "모두 다시쓰기"}
        aria-label="모두 다시쓰기"
      >
        <span class="ai-label">AI</span>
        <RefreshCw size={iconSize} />
      </button>
    {/if}
    
    <!-- Expand All -->
    {#if showExpand && onExpandAll}
      <button
        class="ai-action-btn ai-tool-btn has-ai-label"
        onclick={onExpandAll}
        disabled={isProcessing}
        title={isProcessing ? "처리 중..." : "모두 늘리기"}
        aria-label="모두 늘리기"
      >
        <span class="ai-label">AI</span>
        <Maximize2 size={iconSize} />
      </button>
    {/if}
    
    <!-- Shrink All -->
    {#if showShrink && onShrinkAll}
      <button
        class="ai-action-btn ai-tool-btn has-ai-label"
        onclick={onShrinkAll}
        disabled={isProcessing}
        title={isProcessing ? "처리 중..." : "모두 줄이기"}
        aria-label="모두 줄이기"
      >
        <span class="ai-label">AI</span>
        <Minimize2 size={iconSize} />
      </button>
    {/if}
  </div>
  
  <!-- Stats display (for SplitView mode) -->
  {#if hasAIStats}
    <div class="ai-stats">
      {#if addedChars > 0}
        <span class="stat-add">+{addedChars}</span>
      {/if}
      {#if deletedChars > 0}
        <span class="stat-del">-{deletedChars}</span>
      {/if}
    </div>
  {/if}
</div>

<!-- Bulk Prompt Input (shown below header when active) -->
{#if localShowBulkPrompt && onBulkPromptSubmit}
  <BulkPromptInput
    show={localShowBulkPrompt}
    bind:promptInput={bulkPromptInput}
    placeholder="전체 텍스트를 어떻게 변경할까요?"
    onSubmit={handleBulkPromptSubmit}
    onClose={() => {
      savedBulkPromptInput = bulkPromptInput
      onBulkPromptClose?.()
    }}
    bind:modelId={currentModelId}
    onModelChange={(modelId) => onModelChange?.(modelId)}
  />
{/if}

<style>
  .ai-actions-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px;
    margin-bottom: 8px;
    background: var(--bg-secondary);
    border-radius: 8px;
  }
  
  .ai-actions-toolbar {
    display: flex;
    align-items: center;
    gap: 2px;
  }
  
  .ai-action-btn {
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: var(--text-tertiary);
    cursor: pointer;
    transition: all 0.12s ease;
    position: relative;
  }
  
  .ai-action-btn :global(svg) {
    width: 16px;
    height: 16px;
    stroke-width: 1.5;
  }
  
  .ai-action-btn:hover:not(:disabled) {
    background: var(--bg-hover, rgba(0, 0, 0, 0.04));
    color: var(--text-primary);
  }
  
  .ai-action-btn:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  .ai-action-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  .ai-action-apply {
    color: var(--success);
  }
  
  .ai-action-apply:hover:not(:disabled) {
    background: rgba(34, 197, 94, 0.08);
    color: var(--success);
  }
  
  .ai-action-reject {
    color: var(--text-tertiary);
  }
  
  .ai-action-reject:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.08);
    color: var(--error);
  }
  
  .ai-tool-btn {
    position: relative;
  }
  
  .ai-action-btn.has-ai-label {
    position: relative;
  }
  
  .ai-label {
    position: absolute;
    bottom: -1px;
    right: -1px;
    font-size: 6px;
    font-weight: 600;
    letter-spacing: 0.2px;
    color: var(--accent-primary);
    background: transparent;
    padding: 0;
    line-height: 1;
    opacity: 0.7;
    pointer-events: none;
  }
  
  .toolbar-separator {
    width: 1px;
    height: 16px;
    background: var(--border-primary);
    margin: 0 4px;
    opacity: 0.3;
  }
  
  /* Stats display */
  .ai-stats {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
  }
  
  .stat-add {
    color: var(--success);
  }
  
  .stat-del {
    color: var(--error);
  }
  
  /* Dark theme adjustments */
  :global(:root.dark-theme) .ai-action-btn:hover:not(:disabled) {
    background: var(--bg-hover, rgba(255, 255, 255, 0.08));
  }
  
  :global(:root.dark-theme) .toolbar-separator {
    background: var(--border-secondary);
    opacity: 0.2;
  }
</style>